// ── TEST-SITE-ONLY stage resets ──────────────────────────────────────────────
// Roll the current tournament back to an earlier lifecycle stage while KEEPING
// the full team roster, so a demo can be run forward again and again. Three
// stages: Registration → Qualify → Built Tournament (flights/tee times set, not
// yet played). The Calcutta auction reset is a SEPARATE feature and is left
// untouched here.
//
// HARD GATE: every function refuses to run unless IS_TEST_SITE (the DB URL is
// the pbcc-tourney-test instance). The UI also hides the buttons off-test, so
// this can neither appear nor act on pbccgolf.com.
//
// Roster source of truth: this demo was built directly into flights (no
// /registrations node), so the roster lives in flights/*/teams/*/info. We derive
// it from /registrations when present, else from the flight teams — either way
// the same 104 teams survive every reset.

import { get, ref, update } from 'firebase/database'

import { db } from './firebase.js'
import { normalizeTeamKey, generateBracket } from './data.js'
import { IS_TEST_SITE } from '../config/siteMode.js'

function assertTest() {
  if (!IS_TEST_SITE) throw new Error('Stage resets are TEST SITE only — refusing to run.')
}

async function loadTournament(tid) {
  const snap = await get(ref(db, `tournaments/${tid}`))
  return snap.val() || {}
}

// Canonical roster: [{ teamName, players, qualifyingScore|null }], deduped by
// the same normalized key the build/qualify flows use.
function extractRoster(tournament) {
  const out = []
  const seen = new Set()
  const add = (teamName, players, qualifyingScore) => {
    const name = teamName || (players || []).join('/')
    const key = normalizeTeamKey(name)
    if (!key || seen.has(key)) return
    seen.add(key)
    out.push({
      teamName: name,
      players: Array.isArray(players) ? players.filter(Boolean) : [],
      qualifyingScore: Number.isFinite(Number(qualifyingScore)) ? Number(qualifyingScore) : null,
    })
  }
  const regs = tournament.registrations || {}
  if (Object.keys(regs).length) {
    for (const r of Object.values(regs)) add(r?.teamName, r?.players, r?.qualifyingScore)
  } else {
    for (const f of Object.values(tournament.flights || {})) {
      for (const t of Object.values(f?.teams || {})) {
        const i = t?.info || {}
        add(i.name, i.players, i.qualifyingScore)
      }
    }
  }
  return out
}

// Shared: null out every stage node downstream of registration.
function wipeDownstream(updates, tid) {
  updates[`tournaments/${tid}/flights`] = null
  updates[`tournaments/${tid}/teeTimes`] = null
  updates[`tournaments/${tid}/brackets`] = null
  updates[`tournaments/${tid}/results`] = null
  updates[`tournaments/${tid}/tracking`] = null
  updates[`tournaments/${tid}/teamTokens`] = null
}

// 1) REGISTRATION — teams registered, no qualifying scores, nothing built.
export async function resetToRegistration(tid) {
  assertTest()
  const t = await loadTournament(tid)
  const roster = extractRoster(t)
  const updates = {}
  wipeDownstream(updates, tid)
  updates[`tournaments/${tid}/qualifying`] = null
  const base = Date.now()
  const regs = {}
  roster.forEach((r, i) => {
    regs[`reg_${String(i + 1).padStart(3, '0')}`] = {
      teamName: r.teamName,
      players: r.players,
      registeredAt: base + i,
    }
  })
  updates[`tournaments/${tid}/registrations`] = regs
  updates[`tournaments/${tid}/info/stage`] = null
  updates[`tournaments/${tid}/info/status`] = 'open'
  updates[`tournaments/${tid}/info/closedAt`] = null
  updates[`tournaments/${tid}/info/flightsLocked`] = false
  updates[`tournaments/${tid}/info/qualifyingClosed`] = false
  updates[`tournaments/${tid}/info/qualifyingClosedAt`] = null
  await update(ref(db), updates)
  return roster.length
}

// 2) QUALIFY — teams registered WITH their qualifying scores submitted, nothing
// built. Mirrors scores into the live /qualifying pool so the Qualifying tab
// shows them; the normal Accept → Build flow then runs forward.
export async function resetToQualify(tid) {
  assertTest()
  const t = await loadTournament(tid)
  const roster = extractRoster(t)
  const qualDate = t.info?.date || null
  const updates = {}
  wipeDownstream(updates, tid)
  const base = Date.now()
  const regs = {}
  const qualifying = {}
  roster.forEach((r, i) => {
    const n = String(i + 1).padStart(3, '0')
    const hasScore = Number.isFinite(Number(r.qualifyingScore))
    regs[`reg_${n}`] = {
      teamName: r.teamName,
      players: r.players,
      registeredAt: base + i,
      ...(hasScore ? { qualifyingScore: r.qualifyingScore, qualifyingStatus: 'complete', qualifyingDate: qualDate } : {}),
    }
    qualifying[`q_${n}`] = {
      teamName: r.teamName,
      player1: r.players?.[0] || null,
      player2: r.players?.[1] || null,
      players: r.players,
      createdAt: base + i,
      ...(hasScore ? { score: r.qualifyingScore, submittedAt: base + i } : {}),
    }
  })
  updates[`tournaments/${tid}/registrations`] = regs
  updates[`tournaments/${tid}/qualifying`] = qualifying
  updates[`tournaments/${tid}/info/stage`] = null
  updates[`tournaments/${tid}/info/status`] = 'open'
  updates[`tournaments/${tid}/info/closedAt`] = null
  updates[`tournaments/${tid}/info/flightsLocked`] = false
  updates[`tournaments/${tid}/info/qualifyingClosed`] = false
  updates[`tournaments/${tid}/info/qualifyingClosedAt`] = null
  await update(ref(db), updates)
  return roster.length
}

// 3) BUILT TOURNAMENT — flights & tee times stay set; strip everything from the
// "played" stage so it's ready to be played again. Preserves the flight
// structure, pairings (opponentId) and access tokens.
export async function resetToTournament(tid) {
  assertTest()
  const t = await loadTournament(tid)
  const flights = t.flights || {}
  const flightIds = Object.keys(flights)
  if (!flightIds.length) {
    throw new Error('Nothing is built yet — use “Reset to Registration/Qualify”, then Build the field, to reach the tournament stage.')
  }
  const updates = {}
  updates[`tournaments/${tid}/results`] = null
  updates[`tournaments/${tid}/tracking`] = null
  for (const [fid, f] of Object.entries(flights)) {
    for (const teamId of Object.keys(f?.teams || {})) {
      const p = `tournaments/${tid}/flights/${fid}/teams/${teamId}`
      updates[`${p}/scores`] = null
      updates[`${p}/info/locked`] = null
      updates[`${p}/info/reportedWinnerId`] = null
      updates[`${p}/info/reportedAt`] = null
    }
  }
  updates[`tournaments/${tid}/info/status`] = 'open'
  updates[`tournaments/${tid}/info/closedAt`] = null
  updates[`tournaments/${tid}/info/stage`] = 'active'
  updates[`tournaments/${tid}/info/flightsLocked`] = true
  await update(ref(db), updates)
  // Regenerate each bracket to clear match winners (re-seeds from opponentId).
  if (t.brackets) {
    const bracketDays = Number(t.info?.bracketDays) || 3
    for (const fid of flightIds) {
      try { await generateBracket(tid, fid, bracketDays) } catch { /* tolerate short/odd flights */ }
    }
  }
  return flightIds.length
}
